import {extractAllTables, extractTable} from 'helpers/markdownTable';

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\([^)]+\)/g;

export type AnswerTable = {
  headers: string[];
  rows: string[][];
  startIdx: number;
};

/** Flatten markdown cell text for CSV (e.g. `[Name](/people/x)` → `Name`). */
export const stripMarkdownCell = (text: string): string =>
  text.replace(MARKDOWN_LINK_PATTERN, '$1').trim();

const toAnswerTable = (table: {
  startIdx: number;
  header: string[];
  rows: string[][];
}): AnswerTable => ({
  startIdx: table.startIdx,
  headers: table.header.map(stripMarkdownCell),
  rows: table.rows.map((row) => row.map(stripMarkdownCell)),
});

/** Parse every GFM table in the answer markdown. */
export const parseAnswerTables = (markdown: string): AnswerTable[] =>
  extractAllTables(markdown.split('\n'))
    .filter((table) => table.header.length > 0)
    .map(toAnswerTable);

export const parseAnswerTable = (
  markdown: string
): {headers: string[]; rows: string[][]} | null => {
  const table = parseAnswerTables(markdown)[0];
  if (!table) {
    return null;
  }
  return {headers: table.headers, rows: table.rows};
};

const headingAboveTable = (lines: string[], tableStartIdx: number): string => {
  for (let i = tableStartIdx - 1; i >= 0; i -= 1) {
    const match = lines[i].trim().match(/^#{1,3}\s+(.+)$/);
    if (match) {
      return match[1].trim();
    }
  }
  return 'Results';
};

/** Label from the nearest heading above the table, or ``Results.csv``. */
export const answerCsvDisplayFilename = (
  markdown: string,
  table?: {startIdx: number; index: number; total: number}
): string => {
  const lines = markdown.split('\n');
  const startIdx = table?.startIdx ?? extractTable(lines)?.startIdx;
  if (startIdx == null) {
    return 'Results.csv';
  }

  const rawHeading = headingAboveTable(lines, startIdx);
  const cleaned = rawHeading.replace(/[^a-zA-Z0-9]+/g, ' ').trim() || 'Results';

  let suffix = '';
  if (table && table.total > 1) {
    const tablesWithSameHeading = extractAllTables(lines).filter(
      (candidate) => headingAboveTable(lines, candidate.startIdx) === rawHeading
    );
    if (tablesWithSameHeading.length > 1) {
      const position = tablesWithSameHeading.findIndex(
        (candidate) => candidate.startIdx === startIdx
      );
      if (position > 0) {
        suffix = ` ${position + 1}`;
      }
    }
  }

  return `${cleaned}${suffix}.csv`;
};
